import { useState, useEffect, useCallback, useMemo } from "react";
import type { Client, Entry, Config } from "@ascente/shared-types";
import { fmt, calculateGST } from "../../utils/formatters.js";
import { parseDate, toDateInput } from "../../utils/dateHelpers.js";
import { calcFixedFeeAmount, calcTaskCharges } from "../../utils/billing.js";
import { useApp } from "../../context/AppContext.js";

export interface InvoiceDraft {
  client: Client;
  entries: Entry[];
  startDate: string;
  endDate: string;
  invoiceNumber: string;
}

export interface LineItem {
  id: string;
  desc: string;
  qty: number;
  unit: string;
  rate: number;
  amount: number;
  section: string;
}

export interface BuildLineItemsArgs {
  client: Client;
  entries: Entry[];
  rangeStart: Date;
  rangeEnd: Date;
  mode: "detailed" | "simplified";
  someMonthsOptIn: string[];
}

export function buildLineItems(args: BuildLineItemsArgs): LineItem[] {
  const { client, entries, rangeStart, rangeEnd, mode, someMonthsOptIn } = args;
  const items: LineItem[] = [];
  const billsHourly = client.type === "hourly" || client.type === "hourly_fixed";

  if (mode === "simplified") {
    if (billsHourly) {
      const hours = entries.reduce((s, e) => s + e.hours, 0);
      const amount = hours * client.rate;
      if (amount > 0) {
        items.push({ id: "simplified-services", desc: "Marketing & Design Services",
          qty: hours, unit: "hr", rate: client.rate, amount, section: "simplified" });
      }
    } else {
      const amount = calcTaskCharges(entries);
      if (amount > 0) {
        items.push({ id: "simplified-services", desc: "Marketing & Design Services",
          qty: 1, unit: "", rate: amount, amount, section: "simplified" });
      }
    }
  } else if (billsHourly) {
    entries.forEach((e) => items.push({ id: e.id, desc: e.task, qty: e.hours,
      unit: "hr", rate: client.rate, amount: e.hours * client.rate, section: "hourly" }));
  } else {
    entries.forEach((e) => {
      const qty = e.hours || 1;
      items.push({ id: e.id, desc: e.task, qty, unit: "", rate: e.taskCharge || 0,
        amount: qty * (e.taskCharge || 0), section: "task" });
    });
  }

  // Fixed fees always list separately, in both modes.
  client.fixedFees.forEach((f, i) => {
    const amount = calcFixedFeeAmount(f, entries, rangeStart, rangeEnd, someMonthsOptIn);
    if (amount === 0) return;
    const qty = amount / f.amount;
    items.push({ id: `fixed-${i}`, desc: f.name, qty,
      unit: f.frequency === "monthly" && qty > 1 ? "mo" : "",
      rate: f.amount, amount, section: "fixed" });
  });

  return items;
}

/** Format a date string as "January 1, 2024". */
function fmtDateLong(dateStr: string): string {
  const d = parseDate(dateStr);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

interface Props {
  draft: InvoiceDraft;
  config: Partial<Config>;
  currentUser: string;
  onClose: () => void;
}

export function InvoicePreview({ draft, config, currentUser, onClose }: Props) {
  const { handleSaveSettings } = useApp();
  const [invoiceNumber, setInvoiceNumber] = useState(draft.invoiceNumber);
  const [companyName, setCompanyName] = useState(config.companyName ?? "");
  const [companyContact, setCompanyContact] = useState(config.companyContact ?? "");
  const [clientName, setClientName] = useState(draft.client.name);
  const [clientEmail, setClientEmail] = useState(draft.client.email ?? "");
  const [invoiceDate, setInvoiceDate] = useState(toDateInput(new Date()));
  const [dueDate, setDueDate] = useState("");
  const [billingPeriod, setBillingPeriod] = useState(
    draft.startDate && draft.endDate
      ? `${fmtDateLong(draft.startDate)} – ${fmtDateLong(draft.endDate)}`
      : "",
  );
  const [paymentMethod, setPaymentMethod] = useState(config.paymentMethod ?? "e-transfer");
  const [discount, setDiscount] = useState(0);
  const [footerNote, setFooterNote] = useState("");
  const [mode, setMode] = useState<"detailed" | "simplified">("detailed");
  const [preparedBy, setPreparedBy] = useState(currentUser);
  const [someMonthsOptIn, setSomeMonthsOptIn] = useState<string[]>([]);
  const [businessInfoSaveState, setBusinessInfoSaveState] = useState<"idle" | "saved" | "error">(
    "idle",
  );

  const someMonthsFees = draft.client.fixedFees.filter((f) => f.frequency === "some_months");

  const toggleSomeMonths = (name: string) => {
    setSomeMonthsOptIn((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  // Memoized so the Date objects stay referentially stable across renders
  // unless the underlying draft range strings actually change.
  const rangeStart = useMemo(() => parseDate(draft.startDate), [draft.startDate]);
  const rangeEnd = useMemo(() => parseDate(draft.endDate), [draft.endDate]);

  const rebuildLineItems = useCallback(
    () =>
      buildLineItems({
        client: draft.client,
        entries: draft.entries,
        rangeStart,
        rangeEnd,
        mode,
        someMonthsOptIn,
      }),
    [draft.client, draft.entries, rangeStart, rangeEnd, mode, someMonthsOptIn],
  );

  const [lineItems, setLineItems] = useState<LineItem[]>(() => rebuildLineItems());
  useEffect(() => setLineItems(rebuildLineItems()), [rebuildLineItems]);

  const updateItem = (i: number, patch: Partial<LineItem>) =>
    setLineItems((prev) => prev.map((item, idx) => (idx === i ? { ...item, ...patch } : item)));
  const removeItem = (i: number) => setLineItems((prev) => prev.filter((_, idx) => idx !== i));
  const addItem = () =>
    setLineItems((prev) => [
      ...prev,
      { id: `manual-${prev.length}-${Date.now()}`, desc: "", qty: 1, unit: "", rate: 0, amount: 0, section: "manual" },
    ]);

  const subtotal = lineItems.reduce((s, item) => s + item.amount, 0);
  const discountAmount = subtotal * (discount / 100);
  const afterDiscount = subtotal - discountAmount;
  const gst = calculateGST(afterDiscount);
  const total = afterDiscount + gst;

  const handleSaveBusinessInfo = async () => {
    // Route through the context's non-destructive per-user write, not
    // api.saveConfig — that handler clears the whole Config sheet and
    // writes back only the keys it is given. Send the full settings shape
    // (falling back to what is already loaded) so this save cannot blank
    // out the fields the invoice screen does not edit.
    const ok = await handleSaveSettings({
      companyName,
      companyContact,
      paymentMethod,
      etransferEmail: config.etransferEmail ?? "",
      chequeAddress: config.chequeAddress ?? "",
    });
    setBusinessInfoSaveState(ok ? "saved" : "error");
    setTimeout(() => setBusinessInfoSaveState("idle"), 2000);
  };

  const handlePrint = () => {
    const html = buildPrintHtml();
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.print();
  };

  const buildPrintHtml = () => `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Invoice ${invoiceNumber}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'DM Sans', sans-serif; color: #1a1a2e; padding: 40px; }
    .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
    .inv-meta { text-align: right; }
    .inv-meta h1 { font-size: 28px; color: #2563eb; }
    .section { margin-bottom: 24px; }
    table { width: 100%; border-collapse: collapse; }
    th { border-bottom: 2px solid #e5e7eb; padding: 8px; text-align: left; font-size: 13px; color: #6b7280; }
    td { padding: 10px 8px; border-bottom: 1px solid #f3f4f6; font-size: 14px; }
    .right { text-align: right; }
    .totals { margin-top: 16px; text-align: right; }
    .totals div { margin-bottom: 4px; font-size: 14px; }
    .grand-total { font-size: 18px; font-weight: 700; color: #2563eb; margin-top: 8px; }
    .footer { margin-top: 40px; font-size: 12px; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 16px; }
    @media print { @page { margin: 20mm; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <strong style="font-size:20px">${companyName}</strong>
      <pre style="font-size:13px;white-space:pre-wrap;margin-top:8px;color:#4b5563">${companyContact}</pre>
    </div>
    <div class="inv-meta">
      <h1>INVOICE</h1>
      <div style="margin-top:8px;font-size:13px">
        <div><strong>Invoice #:</strong> ${invoiceNumber}</div>
        <div><strong>Date:</strong> ${invoiceDate}</div>
        ${dueDate ? `<div><strong>Due:</strong> ${dueDate}</div>` : ""}
        ${billingPeriod ? `<div><strong>Period:</strong> ${billingPeriod}</div>` : ""}
      </div>
    </div>
  </div>
  <div class="section">
    <div style="font-size:13px;color:#6b7280;margin-bottom:4px">BILL TO</div>
    <strong>${clientName}</strong>
    ${clientEmail ? `<div style="font-size:13px;color:#4b5563">${clientEmail}</div>` : ""}
  </div>
  <table>
    <thead><tr><th>Description</th><th class="right">Amount</th></tr></thead>
    <tbody>
      ${lineItems.map((item) => `<tr><td>${item.desc}</td><td class="right">${fmt(item.amount)}</td></tr>`).join("")}
    </tbody>
  </table>
  <div class="totals">
    <div>Subtotal: ${fmt(subtotal)}</div>
    ${discount > 0 ? `<div>Discount (${discount}%): -${fmt(discountAmount)}</div>` : ""}
    <div>GST (5%): ${fmt(gst)}</div>
    <div class="grand-total">Total: ${fmt(total)}</div>
  </div>
  <div class="footer">
    <strong>Payment:</strong>
    ${paymentMethod !== "cheque" && config.etransferEmail ? `E-Transfer to ${config.etransferEmail}` : ""}
    ${paymentMethod !== "e-transfer" && config.chequeAddress ? `Cheque payable to: ${config.chequeAddress}` : ""}
    <br/>Prepared by ${preparedBy}
    ${footerNote ? `<br/>${footerNote}` : ""}
  </div>
</body>
</html>`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card invoice-modal" onClick={(e) => e.stopPropagation()}>
        <div className="invoice-modal-header">
          <h3 className="modal-title" style={{ margin: 0 }}>Invoice Preview</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setMode((m) => (m === "detailed" ? "simplified" : "detailed"))}
            >
              {mode === "detailed" ? "Simplified" : "Detailed"}
            </button>
            <button className="btn btn-primary btn-sm" onClick={handlePrint}>Print / Save PDF</button>
            <button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="invoice-modal-body">
          {/* Header fields */}
          <div className="form-row">
            <div className="form-group">
              <label className="inp-label">Invoice #</label>
              <input className="inp" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="inp-label">Invoice Date</label>
              <input type="date" className="inp" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="inp-label">Due Date</label>
              <input type="date" className="inp" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="inp-label">From (Company)</label>
              <input className="inp" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="inp-label">Prepared By</label>
              <input className="inp" value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label className="inp-label">Company Contact Info</label>
              <textarea
                className="inp"
                rows={2}
                placeholder={"Address / Phone / Email"}
                value={companyContact}
                onChange={(e) => setCompanyContact(e.target.value)}
              />
            </div>
            <div className="form-group">
              <button className="btn btn-secondary btn-sm" onClick={() => void handleSaveBusinessInfo()}>
                {businessInfoSaveState === "saved"
                  ? "✓ Saved"
                  : businessInfoSaveState === "error"
                  ? "⚠ Not Saved"
                  : "Save as Default"}
              </button>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="inp-label">Bill To</label>
              <input className="inp" value={clientName} onChange={(e) => setClientName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="inp-label">Client Email</label>
              <input className="inp" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="inp-label">Billing Period</label>
              <input className="inp" value={billingPeriod} onChange={(e) => setBillingPeriod(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="inp-label">Payment Method</label>
              <select className="sel" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as typeof paymentMethod)}>
                <option value="e-transfer">E-Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="both">Both</option>
              </select>
            </div>
          </div>

          {/* Some-months fee opt-in */}
          {someMonthsFees.length > 0 && (
            <div className="form-group" style={{ marginTop: 8 }}>
              <label className="inp-label">Bill This Period&rsquo;s Occasional Fees</label>
              {someMonthsFees.map((fee) => (
                <label key={fee.name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={someMonthsOptIn.includes(fee.name)}
                    onChange={() => toggleSomeMonths(fee.name)}
                  />
                  {fee.name} ({fmt(fee.amount)})
                </label>
              ))}
            </div>
          )}

          {/* Line items */}
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <label className="inp-label" style={{ marginBottom: 0 }}>Line Items</label>
              <button className="btn btn-secondary btn-sm" onClick={addItem}>+ Add Line</button>
            </div>
            {lineItems.map((item, i) => (
              <div key={item.id} className="fee-row">
                <input className="inp" value={item.desc} onChange={(e) => updateItem(i, { desc: e.target.value })} style={{ flex: 3 }} placeholder="Description" />
                <input type="number" className="inp" value={item.amount} min="0" step="0.01" onChange={(e) => updateItem(i, { amount: parseFloat(e.target.value) || 0 })} style={{ width: 120 }} />
                <button className="btn btn-danger btn-sm" onClick={() => removeItem(i)}>✕</button>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="invoice-totals">
            <div className="totals-row"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
            <div className="totals-row">
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Discount %
                <input type="number" className="inp" value={discount} min="0" max="100" step="1" onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)} style={{ width: 80 }} />
              </label>
              <span>-{fmt(discountAmount)}</span>
            </div>
            <div className="totals-row"><span>GST (5%)</span><span>{fmt(gst)}</span></div>
            <div className="totals-row grand"><span>Total</span><span>{fmt(total)}</span></div>
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="inp-label">Footer Note (optional)</label>
            <textarea className="inp" rows={2} value={footerNote} onChange={(e) => setFooterNote(e.target.value)} placeholder="Thank you for your business!" />
          </div>
        </div>
      </div>
    </div>
  );
}
